import Quill from 'quill'
import {
  AlignAction,
  DefaultToolbar,
  type Aligner,
  type Alignment,
  type BlotFormatter,
} from 'quill-blot-formatter'
import { css } from 'styled-components'

export const IMAGE_ALIGN_ATTRIBUTE = 'data-align'

/** `breakText` is the default and is stored as the absence of the align attribute. */
type ImageAlignment = 'left' | 'right' | 'breakText'

export type ImageAlignTitles = Record<ImageAlignment, string>

const FULL_WIDTH = '100%'
const FLOATED_WIDTH = '50%'

/**
 * Floating an image pulls it to the left of whatever text shares its line, so
 * text written before the image has to live in an earlier block to stay above
 * it. Returns the index the image belongs at.
 */
export const startImageBlock = (quill: Quill, index: number): number => {
  const [, offset] = quill.getLine(index)
  if (!offset) return index

  quill.insertText(index, '\n', Quill.sources.USER)
  return index + 1
}

/**
 * A floated image leaves its own block with no line box, so there is nothing
 * there to click on or type into. The line to write beside the image has to be
 * the next block. Returns the index of that line.
 */
export const endImageBlock = (quill: Quill, imageIndex: number): number => {
  // the image and the block's own line break, with nothing else in between
  const imageIsAlone = (quill.getLine(imageIndex)[0]?.length() ?? 0) <= 2
  if (!imageIsAlone) return imageIndex + 1

  const isLastBlock = imageIndex + 2 >= quill.getLength()
  if (isLastBlock) quill.insertText(imageIndex + 1, '\n', Quill.sources.USER)

  return imageIndex + 2
}

const icon = (body: string) => `<svg viewBox="0 0 18 18">${body}</svg>`

const ICONS: Record<ImageAlignment, string> = {
  left: icon(`
    <rect class="ql-fill" x="2" y="4" width="7" height="10"></rect>
    <line class="ql-stroke" x1="11" x2="16" y1="5" y2="5"></line>
    <line class="ql-stroke" x1="11" x2="16" y1="9" y2="9"></line>
    <line class="ql-stroke" x1="11" x2="16" y1="13" y2="13"></line>
  `),
  breakText: icon(`
    <line class="ql-stroke" x1="2" x2="16" y1="3" y2="3"></line>
    <rect class="ql-fill" x="2" y="6" width="14" height="6"></rect>
    <line class="ql-stroke" x1="2" x2="16" y1="15" y2="15"></line>
  `),
  right: icon(`
    <line class="ql-stroke" x1="2" x2="7" y1="5" y2="5"></line>
    <line class="ql-stroke" x1="2" x2="7" y1="9" y2="9"></line>
    <line class="ql-stroke" x1="2" x2="7" y1="13" y2="13"></line>
    <rect class="ql-fill" x="9" y="4" width="7" height="10"></rect>
  `),
}

interface TitledAlignment extends Alignment {
  title: string
}

/**
 * Images are inserted at full width, which leaves no room for text beside them.
 * Only the percentage widths we set ourselves are swapped, so a width the user
 * picked by dragging the resize handles (always in pixels) is never overridden.
 */
const adjustWidth = (image: HTMLElement, alignment: ImageAlignment) => {
  const width = image.getAttribute('width')

  if (alignment === 'breakText') {
    if (width === FLOATED_WIDTH) image.setAttribute('width', FULL_WIDTH)
  } else if (!width || width === FULL_WIDTH) {
    image.setAttribute('width', FLOATED_WIDTH)
  }
}

class ImageAligner implements Aligner {
  private alignments: TitledAlignment[]
  private formatter: BlotFormatter

  constructor(formatter: BlotFormatter, titles: Partial<ImageAlignTitles>) {
    this.formatter = formatter

    const alignment = (name: ImageAlignment): TitledAlignment => ({
      name,
      title: titles[name] ?? '',
      icon: ICONS[name],
      apply: (image: HTMLElement) => this.align(image, name),
    })

    this.alignments = [
      alignment('left'),
      alignment('breakText'),
      alignment('right'),
    ]
  }

  getAlignments(): TitledAlignment[] {
    return this.alignments
  }

  isAligned(image: HTMLElement, alignment: Alignment): boolean {
    const current = image.getAttribute(IMAGE_ALIGN_ATTRIBUTE) ?? 'breakText'
    return current === alignment.name
  }

  clear(image: HTMLElement): void {
    this.align(image, 'breakText')
  }

  private align(image: HTMLElement, alignment: ImageAlignment) {
    if (alignment === 'breakText') {
      image.removeAttribute(IMAGE_ALIGN_ATTRIBUTE)
    } else {
      image.setAttribute(IMAGE_ALIGN_ATTRIBUTE, alignment)
      this.isolateBlock(image)
    }

    adjustWidth(image, alignment)
  }

  private isolateBlock(image: HTMLElement) {
    const quill = this.formatter.quill as Quill
    const blot = Quill.find(image)
    if (!blot || blot instanceof Quill) return

    endImageBlock(quill, startImageBlock(quill, quill.getIndex(blot)))
  }
}

class ImageAlignToolbar extends DefaultToolbar {
  addButtons(
    formatter: BlotFormatter,
    toolbar: HTMLElement,
    aligner: Aligner
  ): void {
    super.addButtons(formatter, toolbar, aligner)

    const alignments = aligner.getAlignments() as TitledAlignment[]
    this.buttons.forEach((button, index) => {
      button.title = alignments[index]?.title ?? ''
    })
  }
}

export class ImageAlignAction extends AlignAction {
  constructor(formatter: BlotFormatter) {
    super(formatter)

    const { titles } = formatter.options.align as {
      titles?: Partial<ImageAlignTitles>
    }

    this.aligner = new ImageAligner(formatter, titles ?? {})
    this.toolbar = new ImageAlignToolbar()
  }
}

export const imageAlignmentStyles = css`
  .ql-editor img[data-align='left'] {
    float: left;
    height: auto;
    margin: 0.25rem 1rem 0.5rem 0;
  }

  .ql-editor img[data-align='right'] {
    float: right;
    height: auto;
    margin: 0.25rem 0 0.5rem 1rem;
  }

  .ql-editor::after {
    content: '';
    display: block;
    clear: both;
  }
`
